import { useState } from 'react'
import { fbt } from 'fbtee'
import { MoreVertical, ShieldCheck, ShieldOff, UserMinus } from 'lucide-react'
import { Button } from '../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import { ConfirmDialog } from '../../components/ConfirmDialog'

/**
 * An admin's kebab on a member row: make admin, remove admin / step down, and
 * remove from the group. The caller decides which apply (see groupRoles.ts);
 * renders nothing when none do.
 */
export function MemberAdminMenu({
  name,
  groupName,
  isAdmin,
  isSelf,
  canPromote,
  canDemote,
  canRemove,
  onSetAdmin,
  onRemove,
}: {
  name: string
  groupName: string
  isAdmin: boolean
  isSelf: boolean
  canPromote: boolean
  canDemote: boolean
  canRemove: boolean
  onSetAdmin: (makeAdmin: boolean) => void
  onRemove: () => void
}) {
  const [confirmRemove, setConfirmRemove] = useState(false)
  const showRole = isAdmin ? canDemote : canPromote
  if (!showRole && !canRemove) return null

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            aria-label={fbt('Member options', 'Accessible label of the admin menu button on a group member row')}
            data-testid="member-admin-menu"
          >
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {showRole && (isAdmin ? (
            <DropdownMenuItem onSelect={() => onSetAdmin(false)}>
              <ShieldOff />
              {isSelf
                ? fbt('Step down as admin', 'Group member menu item for an admin to give up their own admin role')
                : fbt('Remove admin', 'Group member menu item that removes a member’s admin role')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => onSetAdmin(true)}>
              <ShieldCheck />
              {fbt('Make admin', 'Group member menu item that makes a member a group admin')}
            </DropdownMenuItem>
          ))}
          {showRole && canRemove && <DropdownMenuSeparator />}
          {canRemove && (
            <DropdownMenuItem variant="destructive" onSelect={() => setConfirmRemove(true)}>
              <UserMinus />
              {fbt('Remove from group', 'Group member menu item that removes the member from the group')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={fbt(
          'Remove ' + fbt.param('name', name) + '?',
          'Title of the dialog confirming an admin removes a member from the group',
        )}
        description={fbt(
          fbt.param('name', name) + ' will no longer be in ' + fbt.param('group', groupName) +
            '. They can only come back with a new invite.',
          'Description of the dialog confirming an admin removes a member from the group',
        )}
        confirmLabel={fbt('Remove', 'Confirm button that removes a member from the group')}
        onConfirm={() => {
          setConfirmRemove(false)
          onRemove()
        }}
      />
    </>
  )
}
